import { ProductKeys } from "@atlassiansox/asc-stargate";

import { Environment } from "@/libraries/signup/bxpkit-core/src/utils/environment";

import { TemplateData } from "./site-selector-context";

export type TemplateAction =
    | { type: "modal"; templateData: TemplateData }
    | { type: "redirect"; href: string };

export type TemplateActionParams = {
    templateData: TemplateData;
    siteUrls: string[];
    redirectToIdentity?: boolean;
    internalLinkAfterIdentity?: string;
    localizedSignupHref: string;
};

/**
 * Determines whether to open the site selector modal or redirect directly
 * based on user's existing sites and configuration.
 */
export const getTemplateAction = (
    params: TemplateActionParams
): TemplateAction => {
    const {
        templateData,
        siteUrls,
        redirectToIdentity,
        internalLinkAfterIdentity,
        localizedSignupHref
    } = params;

    // If user has existing sites, show modal
    if (siteUrls.length > 0) {
        return { type: "modal", templateData };
    }

    // Otherwise, redirect directly
    const href = getRedirectHref({
        templateData,
        redirectToIdentity,
        internalLinkAfterIdentity,
        localizedSignupHref
    });

    return { type: "redirect", href };
};

type GetRedirectHrefParams = {
    templateData: TemplateData;
    redirectToIdentity?: boolean;
    internalLinkAfterIdentity?: string;
    localizedSignupHref: string;
};

const getRedirectHref = (params: GetRedirectHrefParams): string => {
    const {
        templateData,
        redirectToIdentity,
        internalLinkAfterIdentity,
        localizedSignupHref
    } = params;
    const { templateId, templateName } = templateData;

    if (redirectToIdentity && internalLinkAfterIdentity) {
        return getIdentityUrl(
            templateId,
            templateName,
            internalLinkAfterIdentity
        );
    }

    return getCloudSignupUrl(
        templateData,
        localizedSignupHref,
        internalLinkAfterIdentity
    );
};

const isJiraProduct = (productKey: ProductKeys): boolean => {
    return [
        ProductKeys.JIRA_CORE,
        ProductKeys.JIRA_SERVICE_DESK,
        ProductKeys.JIRA_SOFTWARE,
        ProductKeys.JIRA_PRODUCT_DISCOVERY
    ].includes(productKey);
};

const getCloudSignupUrl = (
    templateData: TemplateData,
    localizedSignupHref: string,
    internalLinkAfterIdentity?: string
): string => {
    const { templateId, templateName, productKey } = templateData;
    let url = `${localizedSignupHref}?product=${productKey}&templateId=${templateId}&edition=free`;

    if (isJiraProduct(productKey)) {
        const urlParams = new URLSearchParams(location.search);
        if (!urlParams.has("p")) {
            urlParams.append("templateModal", templateId);
        }
        url += `&continue=${encodeURIComponent(
            `${location.href}?${urlParams.toString()}`
        )}`;
    } else if (productKey === ProductKeys.CONFLUENCE) {
        const continueUrl = `${Environment.getWacIntegrationUrl()}${
            internalLinkAfterIdentity ?? ""
        }?templateId=${
            templateId +
            encodeURIComponent(
                "&templateName=" + encodeURIComponent(templateName)
            )
        }`;
        url = `${url}&continue=${encodeURIComponent(continueUrl)}`;
    }

    return url;
};

const getIdentityUrl = (
    templateId: string,
    templateName: string,
    internalLinkAfterIdentity: string
): string => {
    const loginUrl = `${Environment.getIdentityBaseUrl()}/login?`;
    const continueUrl = `${Environment.getWacIntegrationUrl()}${internalLinkAfterIdentity}?templateName=${encodeURIComponent(
        templateName
    )}&templateId=${templateId}`;
    return `${loginUrl}&continue=${encodeURIComponent(continueUrl)}`;
};
